import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Spinner,
  Flex,
  FormHelperText,
  HStack,
  Divider,
  Switch,
  Select,
  IconButton,
  Card,
  CardHeader,
  CardBody,
  CardFooter
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const ClaimRulesConfig = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [rules, setRules] = useState([]);

  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const fetchConfig = async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    try {
      const response = await callApi(`/api/admin/config/org/claim-rules`, accessToken);
      if (response.ok) {
        const data = await response.json();
        setRules(data.claim_rules || []);
      } else {
        throw new Error('Failed to fetch configuration');
      }
    } catch (error) {
      toast({
        title: 'Error loading configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [accessToken, toast]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Clean up the rules before saving (split comma-separated strings into arrays)
      const cleanedRules = rules.map(rule => {
        const conditions = { ...rule.conditions };
        
        // Helper to convert comma string to array
        const toArray = (str) => {
          if (!str) return [];
          if (Array.isArray(str)) return str;
          return str.split(',').map(s => s.trim()).filter(s => s.length > 0);
        };

        return {
          ...rule,
          conditions: {
            ...conditions,
            facilities: toArray(conditions.facilities),
            payer_types: toArray(conditions.payer_types),
            surgeons: toArray(conditions.surgeons)
          }
        };
      });

      const response = await callApi('/api/admin/config/org/claim-rules', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ claim_rules: cleanedRules }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      toast({
        title: 'Configuration saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      await fetchConfig();
      
    } catch (error) {
      toast({
        title: 'Error saving configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addRule = () => {
    setRules([
      ...rules,
      {
        rule_name: 'New Rule',
        action: 'skip_tech_claim',
        conditions: {
          facilities: [],
          payer_types: [],
          surgeons: [],
          requires_outpatient: false,
          requires_asc: false
        }
      }
    ]);
  };

  const removeRule = (index) => {
    const newRules = [...rules];
    newRules.splice(index, 1);
    setRules(newRules);
  };

  const updateRule = (index, field, value) => {
    const newRules = [...rules];
    newRules[index][field] = value;
    setRules(newRules);
  };

  const updateCondition = (index, field, value) => {
    const newRules = [...rules];
    newRules[index].conditions[field] = value;
    setRules(newRules);
  };

  // Helper to display array as comma-separated string for editing
  const displayArray = (arr) => {
    if (!arr) return '';
    if (Array.isArray(arr)) return arr.join(', ');
    return arr;
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={6} bg="white" borderRadius="md" shadow="sm">
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="md" mb={2}>Claim Rules Configuration</Heading>
          <Text color="gray.600">
            Configure dynamic rules for claim generation (e.g., suppressing tech claims for specific scenarios).
          </Text>
        </Box>
        <HStack>
          <Button leftIcon={<AddIcon />} colorScheme="blue" variant="outline" onClick={addRule}>
            Add Rule
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>
            Save Configuration
          </Button>
        </HStack>
      </Flex>

      <Divider mb={6} />

      <VStack spacing={6} align="stretch">
        {rules.length === 0 ? (
          <Text color="gray.500" fontStyle="italic">No claim rules configured. Click "Add Rule" to create one.</Text>
        ) : (
          rules.map((rule, index) => (
            <Card key={index} variant="outline" size="sm">
              <CardHeader bg="gray.50" py={3}>
                <Flex justify="space-between" align="center">
                  <Input 
                    value={rule.rule_name} 
                    onChange={(e) => updateRule(index, 'rule_name', e.target.value)}
                    fontWeight="bold"
                    bg="white"
                    w="300px"
                    size="sm"
                  />
                  <IconButton 
                    icon={<DeleteIcon />} 
                    colorScheme="red" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeRule(index)}
                    aria-label="Delete rule"
                  />
                </Flex>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm">Action</FormLabel>
                    <Select 
                      value={rule.action} 
                      onChange={(e) => updateRule(index, 'action', e.target.value)}
                      size="sm"
                    >
                      <option value="skip_tech_claim">Skip Tech Claim Generation</option>
                    </Select>
                  </FormControl>

                  <Divider />
                  <Heading size="xs" color="gray.600" textTransform="uppercase">Conditions (ALL must match)</Heading>

                  <FormControl>
                    <FormLabel fontSize="sm">Facilities (Comma-separated)</FormLabel>
                    <Input 
                      value={displayArray(rule.conditions.facilities)} 
                      onChange={(e) => updateCondition(index, 'facilities', e.target.value)}
                      placeholder="e.g., northern nevada medical center"
                      size="sm"
                    />
                    <FormHelperText fontSize="xs">Leave blank to match any facility. Uses case-insensitive "contains" matching.</FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Surgeons (Comma-separated)</FormLabel>
                    <Input 
                      value={displayArray(rule.conditions.surgeons)} 
                      onChange={(e) => updateCondition(index, 'surgeons', e.target.value)}
                      placeholder="e.g., michael song, lali sekhon"
                      size="sm"
                    />
                    <FormHelperText fontSize="xs">Leave blank to match any surgeon. Uses case-insensitive "contains" matching.</FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Payer Types (Comma-separated)</FormLabel>
                    <Input 
                      value={displayArray(rule.conditions.payer_types)} 
                      onChange={(e) => updateCondition(index, 'payer_types', e.target.value)}
                      placeholder="e.g., medicare, tricare, va"
                      size="sm"
                    />
                    <FormHelperText fontSize="xs">Leave blank to match any payer. Uses case-insensitive "contains" matching.</FormHelperText>
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor={`outpatient-${index}`} mb="0" fontSize="sm">
                      Requires Outpatient Status?
                    </FormLabel>
                    <Switch 
                      id={`outpatient-${index}`}
                      isChecked={rule.conditions.requires_outpatient}
                      onChange={(e) => updateCondition(index, 'requires_outpatient', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor={`asc-${index}`} mb="0" fontSize="sm">
                      Requires ASC Status (POS 24)?
                    </FormLabel>
                    <Switch 
                      id={`asc-${index}`}
                      isChecked={rule.conditions.requires_asc}
                      onChange={(e) => updateCondition(index, 'requires_asc', e.target.checked)}
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
          ))
        )}
      </VStack>
    </Box>
  );
};

export default ClaimRulesConfig;